// Named export: the default export of src/store is the Quasar store
// FACTORY, not the live instance. Importing the default here makes every
// `store.state.main...` read below throw "Cannot read properties of
// undefined (reading 'main')" at render time.
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { LOCATION_INFO } from "../constants";
import COLORS from "../colors";

export const SAMPLE_FIELDS = {
  // Sample info (similar to material items)
  brand: "",
  series: "",
  description: "",
  image: null,

  // Status tracking
  status: "in-office", // in-office, checked-out, ready-for-pickup, discarded

  // Location tracking
  homeLocation: null, // office location key — default destination for Return
  location: {
    lat: null,
    lng: null,
    timestamp: null,
    office: null, // office location key if within geofence
    address: null, // drop-off address, if set manually
    description: "",
  },

  // Current holder info
  lastScannedBy: null, // user ID who last scanned
  borrowerName: "", // optional name of person borrowing
  borrowerContactID: null, // contact ID if the borrower is a known contact

  // Discard info
  discardReason: "",
  discardedBy: null,
  discardedAt: null,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SAMPLE_FIELDS);

export const SAMPLE_STATUS_TYPES = {
  "in-office": {
    label: "In office",
    icon: "home",
    color: "positive",
  },
  "checked-out": {
    label: "Checked out",
    icon: "location",
    color: "warning",
  },
  "ready-for-pickup": {
    label: "Ready for pickup",
    icon: "notification_active",
    color: "info",
  },
  discarded: {
    label: "Discarded",
    icon: "delete",
    color: "negative",
  },
};
deepFreeze(SAMPLE_STATUS_TYPES);

export const SAMPLE_SEARCHABLE_FIELDS = [
  "data.brand",
  "data.series",
  "data.description",
  "data.borrowerName",
  "lastScannedByUser.name",
];
deepFreeze(SAMPLE_SEARCHABLE_FIELDS);

// Geofence radius in meters (roughly 100 meters / 328 feet)
export const GEOFENCE_RADIUS_METERS = 100;

export default class Sample extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SAMPLE_FIELDS);
    super(data);
    this.id = id || null;
  }

  static getPath(id) {
    return id ? `samples/${id}` : "samples";
  }

  get path() {
    return Sample.getPath(this.id);
  }

  // Getters

  get statusInfo() {
    const type =
      SAMPLE_STATUS_TYPES[this.data.status] || SAMPLE_STATUS_TYPES["in-office"];
    return { ...type, ...COLORS[type.color] };
  }

  get isInOffice() {
    return this.data.status === "in-office";
  }

  get isCheckedOut() {
    return this.data.status === "checked-out";
  }

  get isReadyForPickup() {
    return this.data.status === "ready-for-pickup";
  }

  get isDiscarded() {
    return this.data.status === "discarded";
  }

  get lastScannedByUser() {
    return this.data.lastScannedBy
      ? store.state.main.users[this.data.lastScannedBy]
      : null;
  }

  get locationTimestamp() {
    return timestampToDate(this.data.location?.timestamp);
  }

  get officeLocation() {
    return this.data.location?.office
      ? LOCATION_INFO[this.data.location.office]
      : null;
  }

  get homeOffice() {
    return this.data.homeLocation
      ? LOCATION_INFO[this.data.homeLocation] || null
      : null;
  }

  get borrowerContact() {
    return this.data.borrowerContactID
      ? store.state.main.contacts[this.data.borrowerContactID] || null
      : null;
  }

  // Prefer the scaled-down thumbnail the sampleThumbs cloud function writes
  // back onto the doc; fall back to the original upload before it exists.
  get imageThumb() {
    const image = this.data.image;
    return image ? image.thumb || image.src : null;
  }

  get displayTitle() {
    return (
      [this.data.brand, this.data.series].filter(Boolean).join(" ") ||
      "Unnamed Sample"
    );
  }

  get created() {
    return timestampToDate(this.data.created);
  }

  get modified() {
    return timestampToDate(this.data.modified);
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  // QR Code URL - points to the sample scan page
  get qrCodeUrl() {
    const baseUrl = window.location.origin;
    return `${baseUrl}/sample/${this.id}`;
  }

  // Static helper to check if coordinates are within an office geofence
  static getOfficeFromCoordinates(lat, lng) {
    if (!lat || !lng) return null;

    for (const [key, info] of Object.entries(LOCATION_INFO)) {
      if (!info.address?.data?.lat || !info.address?.data?.lng) continue;

      const distance = Sample.calculateDistance(
        lat,
        lng,
        info.address.data.lat,
        info.address.data.lng
      );

      if (distance <= GEOFENCE_RADIUS_METERS) {
        return key;
      }
    }

    return null;
  }

  // Haversine formula to calculate distance between two coordinates in meters
  static calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLng = ((lng2 - lng1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  toString() {
    return this.displayTitle;
  }

  // Firestore converter methods
  static toFirestore(sample) {
    return sample.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Sample(snapshot.id, data);
  }
}
